import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Trailer, TrailerStatus } from 'src/app/services/app/trailer.service';
import { Yard, YardStatus } from 'src/app/services/app/yard.service';

import * as XLSX from 'xlsx';


@Component({
  selector: 'create-instance-dialog',
  templateUrl: './create-instance-dialog.component.html',
  styleUrls: ['./create-instance-dialog.component.css'],
})
export class CreateInstanceDialogComponent implements OnInit {
  uploadedFile: File;
  yardsData: Yard[] = [];
  hideDialog: boolean = false;
  showConfirmation = false;
  currentStep: number = 1;
  instance: number;

  constructor(
    private dialogRef: MatDialogRef<CreateInstanceDialogComponent, Yard[]>
  ) {}

  openDialog(){
    
    this.hideDialog = false;
  }
  closeDialog(){
    this.dialogRef.close();
  }
  nextStep(){
    if(this.currentStep === 1){
      this.currentStep = 2;
    }
  }
  previousStep(){
    if(this.currentStep === 2){
      this.currentStep = 1;
    }
  }

  importData(){
    this.dialogRef.close(this.yardsData);
  }
  closeConfirmationDialog() {
    this.showConfirmation = false;
  }
  /**
   * creates a new instance wich will add the new instance to the user
   * in the user's collection additionally it will upload any data that
   * was submitted by the user to the yard collection and trailer collection
   * respectively.
   */
  createNewInstance() {
    this.dialogRef.close(this.yardsData);
    this.showConfirmation = false;
  }

  handleDragOver(event: DragEvent){
    event.preventDefault();
    let target = event.target as HTMLElement;
    target.classList.add('dragover');
  }
  handleDragLeave(event: DragEvent){
    let target = event.target as HTMLElement;
    target.classList.remove('dragover');
  }
  handleDrop(event: DragEvent){
    event.preventDefault();
    let target = event.target as HTMLElement;
    target.classList.remove('dragover');
    const file = event.dataTransfer?.files[0] as File;
    this.handleFile(file);
    
  }
  /**
   * this handles the users inputs to determine if the files can be handled or not
   * only csv, xlsx and xls files are accepted at the moment.
   * @param event 
   */
  handleFileInput(event: Event) {
    let target = event.target as HTMLInputElement;
    
    let file = target.files[0] as File;
    this.handleFile(file);
  }

  handleFile(file: File){
     if (file) {
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      if (
        fileExtension === 'csv' ||
        fileExtension === 'xlsx' ||
        fileExtension === 'xls'
      ) {
        this.uploadedFile = file;
        this.extractData();

        // File is of allowed type, continue with processing
        // ...
      } else {
        this.uploadedFile = null;
        // File type not allowed, show error message or take appropriate action
      }
    }
  }

  extractData() {
    this.yardsData = [];
    if (this.uploadedFile) {
      this.readUploadedFile(this.uploadedFile);
    }
  }
  /**
   * This function is used the read the extracted data from the file and convert
   * them to Yard objects.
   * @param file
   */
  private readUploadedFile(file: File): void {
    let fileReader = new FileReader();
     let fileName = file.name;
      let fileExtension = fileName.split('.').pop().toLowerCase(); // Get the file extension
      

    fileReader.onload = (e) => {
     
      if(fileExtension === 'csv'){
        let fileContent = fileReader.result as string;
        let data: string[][] = this.processData(fileContent);
        this.yardsData = this.convertExtractedDataToObj(data);
      }
      
      else if(fileExtension === 'xls' || fileExtension === 'xlsx'){
        // Use the xlsx library for Excel files
        
        let arrayBuffer = fileReader.result as ArrayBuffer;
        let data = new Uint8Array(arrayBuffer);
        let workbook = XLSX.read(data, { type: 'array' });

        let sheet = workbook.Sheets[workbook.SheetNames[0]];
        let sheetData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as string[][];
        this.yardsData = this.convertExtractedDataToObj(sheetData);
      }
     
    };
    // this is seperate from the above code.
  
    if (fileExtension === 'csv') {
      fileReader.readAsText(file); // Read as text for CSV files
    } else if (fileExtension === 'xls' || fileExtension === 'xlsx') {
      fileReader.readAsArrayBuffer(file); // Read as ArrayBuffer for Excel files
    }
    
  }

  /**
   * Converts the extracted data of the csv file to a Yard[] object.
   * data[i][0] = location,
   * data[i][1] = trailer_number,
   * data[i][2] = carrier,
   * data[i][3] = status,
   * data[i][4] = po#
   * @param data
   * @returns
   */
  private convertExtractedDataToObj(data: string[][]): Yard[] {
    let yards: Yard[] = [];
    for (let i = 1; i < data.length; ) {
     
      let y: Yard = {
        id: null,
        location:  data[i][0],
        status: data[i][3],
        trailerId: null,
        instance: this.instance,
        trailer: {
          id: null,
          location: data[i][0],
          trailer: data[i][1],
          carrier: data[i][2],
          status: data[i][3],
          po: data[i][4],
          instance: this.instance

        },
      };
    
      // assigning the yard status based on the trailer number and carrier.
      y = this.sortYardStatus(y);
      y.trailer = this.sortTrailerStatus(y.trailer);
      yards.push(y);
      i++;
    }
    // if the location is blank then that value will be omited.
    yards = yards.filter((yard) => {
      return yard.location !== '';
    });
    return yards;
  }
  /**
   * Assigns the status of the yard based on the trailer number and carrier.
   * @param yard
   * @returns
   */
  private sortYardStatus(yard: Yard) {
    if (yard.trailer.trailer === '' && yard.trailer.carrier === '') {
      yard.status = YardStatus.EMPTY;
    } else {
      yard.status = YardStatus.OCCUPIED;
    }
    return yard;
  }
  private sortTrailerStatus(trailer: Trailer){
    if(!trailer){
      return trailer;
    }
    const status = trailer.status === null ? null : trailer.status.toUpperCase();
    switch(trailer.status){
      case 'LOADED':
        trailer.status = TrailerStatus.LOADED;
      break;
      case 'OUT OF SERVICE':
        trailer.status = TrailerStatus.OUT_OF_SERVICE;
      break;
      default:
        trailer.status = TrailerStatus.EMPTY;
      break;
    }
    return trailer;
  }

  private processData(data: string): string[][] {
    let value: string[][] = [];
    let n = 0;
    let r = 0;
    for (let i = 0; i < data.length; i++) {
      if (data[i] === ',' || data[i] === '\n') {
        // values may turn up as ""Hello" or "Hello"" to remove the " the replace function is set
        let temp = data.substring(n, i).replace(/["]+/g, '');
        if (value[r] === undefined) {
          value[r] = [];
        }

        value[r].push(temp);

        if (data[i] === '\n') {
          r++;
        }

        n = i + 1;
      }
    }
    return value;
  }

  ngOnInit(): void {}
}
